import { ReactNode, useMemo } from "react";
import { AioNumber } from "../additional/aioNumber";
import styles from "../aic.module.css";
import { getBaseId } from "./functions/getBaseId";
import {
  AitHeaderCellData,
  AitHeaderRowData,
  AitLocationHeaderCell,
  AitLocationHeaderRow,
  AitTablePosition,
  UPDATE_COLUMN_WIDTH,
} from "./functions/interface";
import { trGetCellFromLocation } from "./functions/trGetCellFromLocation";
import { trGetPositionFromLocation } from "./functions/trGetPositionFromLocation";
import { useTableContext } from "./TableContextProvider";

interface AitCellWidthProps {
  location: AitLocationHeaderCell;
}

/**
 * Control to edit a header cell's width.
 *
 * @template T - Cell payload type
 * @param props - Component props
 * @param props.location - The header cell location
 */
export const AitCellWidth = <T,>({ location }: AitCellWidthProps): ReactNode => {
  const { state, dispatch } = useTableContext<T>();
  const thisCell: AitHeaderCellData<T> | undefined = state?.processedTableData
    ? trGetCellFromLocation(state.processedTableData, location)
    : undefined;
  const id = getBaseId(location, state?.id);

  const displayColWidth = useMemo(() => {
    if (state && location.position === AitTablePosition.headerCell && thisCell) {
      const thisHeaderCell = thisCell as AitHeaderCellData<T>;
      if (thisHeaderCell.colSpan >= 2) {
        const thisHeaderRow = trGetPositionFromLocation(state, {
          ...location,
          position: AitTablePosition.headerRow,
        } as AitLocationHeaderRow).data as AitHeaderRowData<T>;
        /* istanbul ignore else */
        if (thisHeaderRow)
          return {
            width: thisHeaderRow.cells
              .slice(location.column, location.column + thisHeaderCell.colSpan)
              .map(
                (c) =>
                  c.colWidth ??
                  // istanbul ignore next
                  state.defaultCellWidth,
              )
              .reduce((a, b) => a + b, 0),
            calculated: true,
          };
      }
    }
    return { width: thisCell?.colWidth };
  }, [location, state, thisCell]);

  return !state || !thisCell ? null : (
    <div className={styles.aiwBodyRow}>
      <AioNumber
        id={`${id}-width`}
        label="Width (mm)"
        value={
          displayColWidth.width ??
          // istanbul ignore next
          state.defaultCellWidth
        }
        setValue={
          state.editable && !displayColWidth.calculated && !location.isRepeat
            ? (ret) =>
                dispatch({
                  operation: UPDATE_COLUMN_WIDTH,
                  location,
                  value: ret,
                })
            : undefined
        }
      />
    </div>
  );
};

AitCellWidth.displayName = "AitCellWidth";
